<template>
    <div v-if="isActive" class="buy-box d-lg-flex flex-wrap" itemprop="offers" itemscope itemtype="http://schema.org/Offer">
        <div class="product-title pb-3 abbreviated-text" v-if="!isLoading">
            <h1 class="mb-0 abbreviated-text" itemprop="brand">{{ entry.brand.title }} {{ entry.model.title }}</h1>
            <h2 class="mb-4 abbreviated-text display-4" itemprop="model">{{ entry.description }}</h2>
            <span class="product-availablity" v-if="!isDriverStatusInactive">
                <span v-if="entry.available_status == 'AVAILABLE_IN'"><small><font-awesome-icon icon="far fa-truck" /> {{ getAvailableText }}</small></span>
                <span v-if="entry.available_status == 'COMING_SOON'"><small><font-awesome-icon icon="far fa-truck" /> {{ $t('general.availability.coming_soon') }}</small></span>
                <span v-if="entry.available_status == 'NOW_AVAILABLE'"><small><font-awesome-icon icon="far fa-truck" /> {{ $t('general.availability.now_available') }}</small></span>

                <buybox-availablity-info-modal></buybox-availablity-info-modal>
            </span>
            <span v-else>
                {{ $t('pds.buy_box.inactive_driver') }}
            </span>
        </div>
        <div v-else class="w-100">
            <b-skeleton width="80%" height="2rem"></b-skeleton>
            <b-skeleton width="50%" height="1.5rem"></b-skeleton>
        </div>

        <span v-if="!isDriverStatusInactive">
            <div v-if="hasTabs" class="w-100 tabs">
                <b-tabs v-model="activeTab" @input="handleTabChange">
                    <b-tab v-if="subscriptionSingleVehicle" class="input-box w-100" :title="$t('pds.buy_box.payment.single_vehicle_subscription')">
                        <div class="form-group kilometer-package-select mb-3">
                            <label>{{ getMileageForText }} {{ $t('pds.buy_box.mileage_for_text') }}</label>
                            <select name="mileage" id="mileage-desktop" class="form-control custom-select" v-model="mileageSelected"
                                v-if="!isLoading" @change="toggleAnalyticsEvent()">
                                <option v-for="(mileage, index) in mileages" v-bind:value="mileage.mileage">
                                    {{ $n(mileage.mileage, 'number') }} {{ getMileage }} / {{ $t('pds.buy_box.month') }}
                                </option>
                            </select>
                            <b-skeleton type="input" v-else></b-skeleton>
                        </div>
                        <div class="form-group runtime-select mb-3">
                            <label>{{ $t('pds.bux_box.duration') }}</label>
                            <select name="duration" id="duration-desktop" class="form-control custom-select" v-model="durationSelected"
                                v-if="!isLoading" @change="toggleAnalyticsEvent()">
                                <option v-for="(duration, index) in durationOptions" v-bind:value="duration.minimum_duration">
                                    {{ duration.minimum_duration }} {{ duration.minimum_duration === 1 ? $t('pds.buy_box.month') :
                                        $t('pds.buy_box.months') }}
                                </option>
                            </select>
                            <b-skeleton type="input" v-else></b-skeleton>
                        </div>
                        <div class="mt-2" v-if="entry.additional_products && (entry.additional_products.additional_driver || entry.additional_products.dp_reduction)">
                            <label>{{ $t('pds.buy_box.additional_services') }}</label>
                        </div>
                        <div class="form-check mb-3" v-if="entry.additional_products && entry.additional_products.additional_driver">
                            <input v-model="apAdditionalDriver" class="form-check-input" type="checkbox" value="" id="apAdditionalDriver">
                            <label class="form-check-label" for="apAdditionalDriver">
                                {{ entry.additional_products.additional_driver.label }}<br>
                                <small>
                                    {{ this.$root.whitelabel.getFormatetPrice(entry.additional_products.additional_driver.price) }}
                                    / {{ $t('pds.buybox.permonth') }}
                                </small>
                            </label>
                        </div>
                        <div class="form-check mb-3" v-if="entry.additional_products && entry.additional_products.dp_reduction">
                            <input v-model="apDpReduction" class="form-check-input" type="checkbox" value="" id="apDpReduction">
                            <label class="form-check-label" for="apDpReduction">
                                {{ entry.additional_products.dp_reduction.label }}<br>
                                <small>
                                    {{ this.$root.whitelabel.getFormatetPrice(entry.additional_products.dp_reduction.price) }}
                                    / {{ $t('pds.buybox.permonth') }}
                                </small>
                            </label>
                        </div>
                        <div class="price-box mb-4">
                            <span class="price mb-0" v-if="!isLoading">
                                <span>{{ this.$root.whitelabel.getFormatetPrice(getPrice) }}</span>
                                <span class="per-month">/ {{ $t('pds.buybox.permonth') }}</span>
                            </span>
                            <b-skeleton height="2rem" width="150px" v-else></b-skeleton>
                            <small class="mb-3" v-if="showVat">
                                <span v-if="withoutTax">{{ $t('pds.buybox.netAllInclusive') }} </span>
                                <span v-else>{{ $t('pds.buybox.allInclusive') }} </span>
                            </small>
                        </div>
                        <button type="button" class="btn btn-primary btn-block mt-3" v-if="!isLoading && !initLoading && entry.available_status != 'COMING_SOON'"
                            @click="initCheckout">
                            {{ $t('pds.buy_box.subscribe') }}
                        </button>
                        <button type="button" class="btn btn-primary btn-block mt-3 disabled" v-if="initLoading && entry.available_status != 'COMING_SOON'">
                            {{ $t('pds.buy_box.loading') }}
                        </button>
                        <b-skeleton width="100%" height="38px" v-if="isLoading"></b-skeleton>
                    </b-tab>
                    <b-tab v-if="subscriptionMultiVehicle && hasTier" class="input-box w-100" :title="$t('pds.buy_box.payment.multi_vehicle_subscription')">
                        <div class="form-group kilometer-package-select mb-3">
                            <div class="w-100">
                                <b-badge class="image-badge badge-mvs-title">{{ entry.tier.title }}</b-badge>
                            </div>
                            <div class="mb-4">
                                {{ $t('pds.buy_box.tier.info') }}
                            </div>

                            <div class="mb-4">{{ getMileageForText }} {{ $t('pds.buy_box.mileage_for_text') }}: {{ entry.tier.mileage }}</div>
                            <div class="mb-4">{{ $t('pds.bux_box.duration') }}: {{ entry.tier.month }}</div>
                        </div>
                        <div class="price-box mb-4">
                            <span class="price mb-0" v-if="!isLoading">
                                <span>{{ this.$root.whitelabel.getFormatetPrice(getTierPrice) }}</span>
                                <span class="per-month">/ {{ $t('pds.buybox.permonth') }}</span>
                            </span>
                            <b-skeleton height="2rem" width="150px" v-else></b-skeleton>
                            <small class="mb-3" v-if="showVat">
                                <span v-if="withoutTax">{{ $t('pds.buybox.netAllInclusive') }} </span>
                                <span v-else>{{ $t('pds.buybox.allInclusive') }} </span>
                            </small>
                        </div>
                        <button type="button" class="btn btn-primary btn-block mt-3" v-if="!isLoading && !initLoading && entry.available_status != 'COMING_SOON'"
                            @click="initCheckout">
                            {{ $t('pds.buy_box.subscribe_tier') }}
                        </button>
                        <button type="button" class="btn btn-primary btn-block mt-3 disabled" v-if="initLoading && entry.available_status != 'COMING_SOON'">
                            {{ $t('pds.buy_box.loading') }}
                        </button>
                        <b-skeleton width="100%" height="38px" v-if="isLoading"></b-skeleton>
                    </b-tab>
                    <b-tab v-if="subscriptionDailyRental" class="input-box w-100" :title="$t('pds.buy_box.payment.daily_rental_subscription')">
                        <buy-box-subscription-daily-rental :entry="entry"></buy-box-subscription-daily-rental>
                    </b-tab>
                </b-tabs>
            </div>

            <div v-else class="w-100">
                <div v-if="subscriptionSingleVehicle" class="input-box w-100">
                    <div class="form-group kilometer-package-select mb-3">
                        <label>{{ getMileageForText }} {{ $t('pds.buy_box.mileage_for_text') }}</label>
                        <select name="mileage" id="mileage-desktop" class="form-control custom-select" v-model="mileageSelected"
                            v-if="!isLoading" @change="toggleAnalyticsEvent()">
                            <option v-for="(mileage, index) in mileages" v-bind:value="mileage.mileage">
                                {{ $n(mileage.mileage, 'number') }} {{ getMileage }} / {{ $t('pds.buy_box.month') }}
                            </option>
                        </select>
                        <b-skeleton type="input" v-else></b-skeleton>
                    </div>

                    <div class="form-group runtime-select mb-3">
                        <label>{{ $t('pds.bux_box.duration') }}</label>
                        <select name="duration" id="duration-desktop" class="form-control custom-select" v-model="durationSelected"
                            v-if="!isLoading" @change="toggleAnalyticsEvent()">
                            <option v-for="(duration, index) in durationOptions" v-bind:value="duration.minimum_duration">
                                {{ duration.minimum_duration }} {{ duration.minimum_duration === 1 ? $t('pds.buy_box.month') :
                                    $t('pds.buy_box.months') }}
                            </option>
                        </select>
                        <b-skeleton type="input" v-else></b-skeleton>
                    </div>

                    <div class="mt-2" v-if="entry.additional_products && (entry.additional_products.additional_driver || entry.additional_products.dp_reduction)">
                        <label>{{ $t('pds.buy_box.additional_services') }}</label>
                    </div>
                    <div class="form-check mb-3" v-if="entry.additional_products && entry.additional_products.additional_driver">
                        <input v-model="apAdditionalDriver" class="form-check-input" type="checkbox" value="" id="apAdditionalDriver">
                        <label class="form-check-label" for="apAdditionalDriver">
                            {{ entry.additional_products.additional_driver.label }}<br>
                            <small>
                                {{ this.$root.whitelabel.getFormatetPrice(entry.additional_products.additional_driver.price) }}
                                / {{ $t('pds.buybox.permonth') }}
                            </small>
                        </label>
                    </div>
                    <div class="form-check mb-3" v-if="entry.additional_products && entry.additional_products.dp_reduction">
                        <input v-model="apDpReduction" class="form-check-input" type="checkbox" value="" id="apDpReduction">
                        <label class="form-check-label" for="apDpReduction">
                            {{ entry.additional_products.dp_reduction.label }}<br>
                            <small>
                                {{ this.$root.whitelabel.getFormatetPrice(entry.additional_products.dp_reduction.price) }}
                                / {{ $t('pds.buybox.permonth') }}
                            </small>
                        </label>
                    </div>

                    <div class="price-box mb-4" itemprop="price">
                        <span class="price mb-0" v-if="!isLoading">
                            <span>{{ this.$root.whitelabel.getFormatetPrice(getPrice) }}</span>
                            <span class="per-month">/ {{ $t('pds.buybox.permonth') }}</span>
                        </span>
                        <b-skeleton height="2rem" width="150px" v-else></b-skeleton>
                        <small class="mb-3" v-if="showVat">
                            <span v-if="withoutTax">{{ $t('pds.buybox.netAllInclusive') }} </span>
                            <span v-else>{{ $t('pds.buybox.allInclusive') }} </span>
                        </small>
                    </div>
                    <button type="button" class="btn btn-primary btn-block mt-3" v-if="!isLoading && !initLoading && entry.available_status != 'COMING_SOON'"
                        @click="initCheckout">
                        {{ $t('pds.buy_box.subscribe') }}
                    </button>
                    <button type="button" class="btn btn-primary btn-block mt-3 disabled" v-if="initLoading && entry.available_status != 'COMING_SOON'">
                        {{ $t('pds.buy_box.loading') }}
                    </button>
                    <b-skeleton width="100%" height="38px" v-if="isLoading"></b-skeleton>
                </div>
                <div v-else-if="subscriptionMultiVehicle && hasTier" class="input-box w-100">
                    <div class="form-group kilometer-package-select mb-3">
                            <div class="w-100">
                                <b-badge class="image-badge badge-mvs-title">{{ entry.tier.title }}</b-badge>
                            </div>
                            <div class="mb-4">
                                {{ $t('pds.buy_box.tier.info') }}
                            </div>

                            <div class="mb-4">{{ getMileageForText }} {{ $t('pds.buy_box.mileage_for_text') }}: {{ entry.tier.mileage }}</div>
                            <div class="mb-4">{{ $t('pds.bux_box.duration') }}: {{ entry.tier.month }}</div>
                        </div>
                        <div class="price-box mb-4">
                            <span class="price mb-0" v-if="!isLoading">
                                <span>{{ this.$root.whitelabel.getFormatetPrice(getTierPrice) }}</span>
                                <span class="per-month">/ {{ $t('pds.buybox.permonth') }}</span>
                            </span>
                            <b-skeleton height="2rem" width="150px" v-else></b-skeleton>
                            <small class="mb-3" v-if="showVat">
                                <span v-if="withoutTax">{{ $t('pds.buybox.netAllInclusive') }} </span>
                                <span v-else>{{ $t('pds.buybox.allInclusive') }} </span>
                            </small>
                        </div>
                        <button type="button" class="btn btn-primary btn-block mt-3" v-if="!isLoading && !initLoading && entry.available_status != 'COMING_SOON'"
                            @click="initCheckout">
                            {{ $t('pds.buy_box.subscribe_tier') }}
                        </button>
                        <button type="button" class="btn btn-primary btn-block mt-3 disabled" v-if="initLoading && entry.available_status != 'COMING_SOON'">
                            {{ $t('pds.buy_box.loading') }}
                        </button>
                        <b-skeleton width="100%" height="38px" v-if="isLoading"></b-skeleton>
                </div>
                <div v-else-if="subscriptionDailyRental" class="input-box w-100">
                    <buy-box-subscription-daily-rental :entry="entry"></buy-box-subscription-daily-rental>
                </div>
                <div v-else>
                    <p>{{ $t('co.main.error') }}</p>
                </div>
            </div>

            <div class="benefits-box">
                <ul class="list-unstyled">
                    <li><font-awesome-icon icon="fa-check" /> {{ $t('pds.buy_box.all_inclusive_price') }}</li>
                    <li><font-awesome-icon icon="fa-check" /> {{ $t('pds.buy_box.mileage_changeable') }}</li>
                    <li><font-awesome-icon icon="fa-check" /> {{ $t('pds.buy_box.online') }}</li>
                    <li v-if="showSubscriptionFactor">
                        <span id="subscription_factor_tooltip">
                            <font-awesome-icon icon="fa-info-circle" />
                        </span>
                        <b-tooltip target="subscription_factor_tooltip" placement="top">
                            {{ $t('pds.buy_box.subscription_factor.tooltip') }}
                        </b-tooltip>
                        {{ $t('pds.buy_box.subscription_factor') }}: {{ $n(subscriptionFactor, 'number', $wll()) }}
                    </li>
                </ul>
            </div>
        </span>
    </div>
    <div v-else class="buy-box d-lg-flex flex-wrap" itemprop="offers" itemscope="" itemtype="http://schema.org/Offer">
        <div class="product-title pb-3 abbreviated-text" v-if="!isLoading">
            <h1 class="mb-0 abbreviated-text" itemprop="brand">{{ entry.model.brand.title }} {{ entry.model.title }}</h1>
            <h2 class="mb-4 abbreviated-text display-4" itemprop="model">{{ entry.description }}</h2>
        </div>
        <div v-else class="w-100">
            <b-skeleton width="80%" height="2rem"></b-skeleton>
            <b-skeleton width="50%" height="1.5rem"></b-skeleton>
        </div>
        <div class="input-box w-100">
            <partner-contact-box class="mt-4" :entry="entry" :is-loading="isLoading"></partner-contact-box>
        </div>
    </div>
</template>

<script>
import PartnerContactBox from "../../components/molecules/PartnerContactBox.vue";
import BuyboxAvailablityInfoModal from "../../components/molecules/BuyboxAvailablityInfoModal.vue";
import BuyBoxSubscriptionDailyRental from "./BuyBoxSubscriptionDailyRental.vue";
import { BSkeleton, BTooltip, BTab, BTabs, BModal, BBadge} from "bootstrap-vue";
import { gb } from "../../growthbook";

export default {
    name: 'BuyBox',
    components: {
        PartnerContactBox, BuyboxAvailablityInfoModal, BuyBoxSubscriptionDailyRental,
        BModal, BSkeleton, BTooltip, BTab, BTabs, BBadge
    },
    props: {
        entry: null,
        isLoading: true,
        isActive: false,
    },
    data() {
        return {
            isVisible: false,
            initLoading: false,
            isBuilded: false,
            pricePlans: [],
            mileages: [],
            durations: {},
            mileageSelected: '',
            durationSelected: '',
            pricePlanSelected: '',
            initiated: false,
            showVat: this.$root.whitelabel.setting('show_tax'),
            withoutTax: this.$root.whitelabel.setting('show_price_without_taxes'),
            showSubscriptionFactor: this.$root.whitelabel.setting('show_subscription_factor'),
            subscriptionSingleVehicle: 0,
            subscriptionMultiVehicle: 0,
            subscriptionDailyRental: 0,
            activeTab: 0,
            tierId: '',
            apAdditionalDriver: false,
            apDpReduction: false,
        }
    },
    
    methods: {
        initCheckout() {
            let month = this.durationSelected;
            let mileage = this.mileageSelected;
            if (this.entry.private_token === this.$route.query.private_token && this.$route.query.init_checkout == 'true') {
                month = parseInt(this.$route.query.month);
                mileage = parseInt(this.$route.query.mileage);

                localStorage.selectedDuration = month
                localStorage.selectedMileage = mileage
            } else {
                localStorage.selectedDuration = this.durationSelected
                localStorage.selectedMileage = this.mileageSelected
            }
            localStorage.selectedProduct = this.entry.vehicle_uuid
            localStorage.selectedTier = this.tierId

            this.initLoading = true
            
            if (this.$root.user.isLoggedIn()) {
                this.$axios.post('api/v1/checkout/init_checkout', {
                    tier_uuid: this.tierId,
                    vehicle_uuid: this.entry.vehicle_uuid,
                    mileage: mileage,
                    duration: month,
                    hide_taxes: this.$root.whitelabel.setting('show_price_without_taxes'),
                    additional_driver: this.apAdditionalDriver,
                    dp_reduction: this.apDpReduction,
                })
                    .then(response => {
                        if (response.data) {
                            this.$goTo('checkout', { identifier: response.data.data.identifier })
                        } else {
                            this.error = response.data.message
                        }
                    })
                    .catch(error => {
                        this.error = error.response.data.message
                    });
            } else if (this.$root.whitelabel.isLeads()) {
                const val = {
                    mileage_id: this.pricePlanSelected.mileage_id,
                    mileage: this.pricePlanSelected.mileage,
                    duration_id: this.pricePlanSelected.duartion_id,
                    duration: this.pricePlanSelected.minimum_duration,
                    price: this.pricePlanSelected.price,
                    price_without_tax: this.pricePlanSelected.price_without_tax,
                    vehicle_uuid: this.entry.vehicle_uuid

                }
                 this.$goTo('checkout-leads', { identifier: JSON.stringify(val) })
            } else {
                this.$goTo('register', null, { isInCheckout: 'true' })
            }
        },
        groupMileages() {
            return this.sanitizedPricePlans.reduce(function (r, a) {
                r[a.mileage] = r[a.mileage] || [];
                r[a.mileage].push(a);
                return r;
            }, Object.create(null));
        },
        getLowestPricePlan(mileage = null) {
            if (mileage !== null) {
                const lowestPrice = Math.min.apply(Math, this.durations[mileage].map(function (o) { return o.price }))
                const lowestPricePlanForGivenMileage = this.durations[mileage].find(o => o.price === lowestPrice)

                if (typeof lowestPricePlanForGivenMileage !== "undefined" || lowestPricePlanForGivenMileage !== null) {
                    return lowestPricePlanForGivenMileage
                }
            }

            const lowestPrice = Math.min.apply(Math, this.pricePlans.map(function (o) { return o.price }))
            return this.pricePlans.find(o => o.price === lowestPrice);
        },
        getPricePlanByMileageAndDuration(mileage, duration) {
            return this.pricePlans.find(o => o.mileage === mileage && o.minimum_duration === duration)
        },
        toggleAnalyticsEvent() {
            if (process.env.MIX_VUE_APP_ACTIVATE_GOOGLE_ANALYTICS && this.$gtag !== undefined && this.pricePlanSelected) {
                this.$gtag.event('view_item', {
                    currency: this.pricePlanSelected.currency.iso,
                    items: [
                        {
                            item_id: this.entry.id,
                            item_name: this.entry.model.title,
                            item_brand: this.entry.brand.title,
                            item_category: this.entry.category.title,
                            subsidiary_id: this.$root.whitelabel.sub_id(),
                            price: this.pricePlanSelected.price,
                            mileage: this.mileageSelected,
                            duration: this.durationSelected
                        }
                    ]
                })
            }
        },
        handleTabChange(activeTabIndex) {
            if (activeTabIndex === 1 && this.hasTier) {
                this.tierId = this.entry.tier.uuid;
            } else {
                this.tierId = '';
            }
        },
        setTierIdIfNoTabs() {
            if (!this.hasTabs && this.subscriptionMultiVehicle && this.hasTier) {
                this.tierId = this.entry.tier.uuid;
            }
        },
    },
    watch: {
        entry: function (val) {
            this.pricePlans = this.sanitizedPricePlans
            const mileages = this.groupMileages()

            for (const [key, options] of Object.entries(mileages)) {
                this.durations[key] = options
                const option = options.find(o => parseInt(o.mileage) === parseInt(key))
                this.mileages.push({
                    key: option.mileage,
                    mileage: option.mileage
                })
            }

            this.pricePlanSelected = this.getLowestPricePlan()
            this.mileageSelected = this.pricePlanSelected.mileage
            this.durationSelected = this.pricePlanSelected.minimum_duration
            this.isBuilded = true

            this.toggleAnalyticsEvent()
            this.setTierIdIfNoTabs();
        },
        mileageSelected(newMileage, oldMileage) {
            if (newMileage !== oldMileage && this.isBuilded) {
                this.pricePlanSelected = this.getLowestPricePlan(newMileage)
                this.durationSelected = this.pricePlanSelected.minimum_duration
            }
        },
        durationSelected(newDuration, oldDuration) {
            if (newDuration !== oldDuration) {
                this.pricePlanSelected = this.getPricePlanByMileageAndDuration(this.mileageSelected, newDuration)
            }
        },
    },
    computed: {
        getPrice() {
            const netPrice = this.$root.whitelabel.setting('show_price_without_taxes')
            let apCosts = 0
            if (this.apAdditionalDriver) {
                apCosts += netPrice
                    ? this.entry.additional_products.additional_driver.price_net
                    : this.entry.additional_products.additional_driver.price
            }
            if (this.apDpReduction) {
                apCosts += netPrice
                    ? this.entry.additional_products.dp_reduction.price_net
                    : this.entry.additional_products.dp_reduction.price
            }

            return (netPrice ? this.pricePlanSelected.price_without_tax : this.pricePlanSelected.price) + apCosts
        },
        getTierPrice() {
            return parseInt(this.entry.tier.price);
        },
        getAvailableText() {
            if(gb.isOn("vehicle_availibility_updated")) {
                return this.$t('vehicle.availability.text.' + this.entry.available_text)
            } else {
                return this.entry.available_weeks === 1 ? `${this.$t('general.availability.week', { number: this.entry.available_weeks })}` : `${this.$t('general.availability.weeks', { number: this.entry.available_weeks })}`
            }
        },
        durationOptions() {
            return this.durations[this.mileageSelected].sort((a, b) => {
                if (a.minimum_duration < b.minimum_duration)
                    return -1;
                if (a.minimum_duration > b.minimum_duration)
                    return 1;
                return 0;
            });
        },
        isDriverStatusInactive() {
            return this.$root.user?.state?.data?.driver_status === 'INACTIVE'
        },
        sanitizedPricePlans() {
            return this.entry.price_plans.filter(option => option.price > 0)
        },
        durationLabel() {
            return this.entry.fixed_duration ? this.$t('pds.mileage.fixed') : this.$t('pds.mileage.minimum')
        },
        getMileageForText() {
            return `${this.$root.whitelabel.getMileageTextUnit()}`
        },
        getMileage() {
            return `${this.$root.whitelabel.getMileageUnit()}`
        },
        subscriptionFactor() {
            return (this.getPrice / parseFloat(this.entry.listing_price) * 100).toFixed(1)
        },
        hasTier() {
            return typeof this.entry.tier !== 'undefined' &&
                 typeof this.entry.tier === 'object' &&
                 this.entry.tier.hasOwnProperty('mileage') &&
                 this.entry.tier.hasOwnProperty('month') &&
                 this.entry.tier.hasOwnProperty('price') &&
                 this.entry.tier.hasOwnProperty('status') &&
                 this.entry.tier.hasOwnProperty('title') &&
                 this.entry.tier.hasOwnProperty('uuid');
        },
        hasTabs() {
            return this.subscriptionDailyRental + (this.subscriptionMultiVehicle && this.hasTier) + this.subscriptionSingleVehicle > 1;
        },
    },
    created() {
        const availablePaymentOptions = this.$root.whitelabel.setting('payments');

        if (availablePaymentOptions) {
            if (availablePaymentOptions.includes('single_vehicle_subscription')) {
                this.subscriptionSingleVehicle = 1;
            }
            if (availablePaymentOptions.includes('multi_vehicle_subscription')) {
                this.subscriptionMultiVehicle = 1;
            }
            if (availablePaymentOptions.includes('daily_rental_subscription')) {
                this.subscriptionDailyRental = 1;
            }
        }
    },
    beforeUpdate() {
        if (this.$route.query.init_checkout == 'true') {
            this.initCheckout()
        }
    }
}
</script>
